import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

/** Search param a tracked link uses to open a category (`/?category={id}`). */
export const CATEGORY_DEEP_LINK_PARAM = "category";

/**
 * Scrolls once to the category named by `?category=` when the menu lists it, for layouts that show
 * every category on one page; the param is then dropped so back/refresh do not jump again.
 */
export function useCategoryDeepLink(categories: readonly { id: string }[], scrollTo: (categoryId: string) => void) {
  const [searchParams, setSearchParams] = useSearchParams();
  const wanted = searchParams.get(CATEGORY_DEEP_LINK_PARAM);
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current || !wanted || !categories.some((c) => c.id === wanted)) return;
    handled.current = true;
    const frame = window.requestAnimationFrame(() => scrollTo(wanted));
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(CATEGORY_DEEP_LINK_PARAM);
        return next;
      },
      { replace: true },
    );
    return () => window.cancelAnimationFrame(frame);
  }, [wanted, categories, scrollTo, setSearchParams]);
}
